import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { connectToContract } from "./CrownContract";
import CUSDABI from "../abi/CUSD.json";
import DEUSABI from "../abi/DEUS.json";
import { FaExchangeAlt } from "react-icons/fa";
import "../App.css";

const cusdAddress = "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa";
const deusAddress = "0x9a1EE4cC684b75eb847F2CCBDf7DeB26F8f8D6F5";

const TransferActions = ({ provider, account }) => {
  const [activeTab, setActiveTab] = useState("transfer");
  const [selectedToken, setSelectedToken] = useState("CRW");
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [spenderAddress, setSpenderAddress] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [transactionFee, setTransactionFee] = useState("0");

  const tokenConfig = {
    CRW: { contractFn: connectToContract, abi: null },
    CUSD: { address: cusdAddress, abi: CUSDABI },
    DEUS: { address: deusAddress, abi: DEUSABI },
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!provider || !account) return;

      try {
        if (selectedToken === "CRW") {
          const contract = await connectToContract(provider);
          const feeRate = await contract.transactionFeeRate();
          setTransactionFee((Number(feeRate) / 100).toFixed(2)); // CRW: 0-100 = 0-1%
        } else if (selectedToken === "CUSD") {
          const contract = new ethers.Contract(cusdAddress, CUSDABI, provider);
          const feeRate = await contract.transferFeePercent();
          setTransactionFee((Number(feeRate) / 100).toFixed(2)); // CUSD: 0-300 = 0-3%
        } else if (selectedToken === "DEUS") {
          const contract = new ethers.Contract(deusAddress, DEUSABI, provider);
          const feeRate = await contract.transferFeePercent();
          setTransactionFee((Number(feeRate) / 100).toFixed(2)); // DEUS: 0-300 = 0-3%
        }
      } catch (error) {
        console.error("Error fetching transaction fee:", error);
        setTransactionFee("0");
      }
    };
    fetchData();
  }, [provider, account, selectedToken]);

  const getContract = async () => {
    const config = tokenConfig[selectedToken];
    if (selectedToken === "CRW") {
      return await config.contractFn(provider);
    }
    const signer = await provider.getSigner();
    return new ethers.Contract(config.address, config.abi, signer);
  };

  const handleTransfer = async () => {
    if (!provider || !account) {
      alert("Connect your wallet first!");
      return;
    }
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert("Please enter a valid positive amount!");
      return;
    }
    if (!ethers.isAddress(toAddress)) {
      alert("Please enter a valid address!");
      return;
    }
    try {
      const contract = await getContract();
      const amountInBaseUnits = ethers.parseEther(amount);
      const tx = await contract.transfer(toAddress, amountInBaseUnits);
      await tx.wait();
      alert(`${selectedToken} Transfer successful!`);
      setToAddress("");
      setAmount("");
    } catch (error) {
      console.error(`${selectedToken} Transfer failed:`, error);
      alert(`${selectedToken} Transfer failed: ${error.message}`);
    }
  };

  const handleApprove = async () => {
    if (!provider || !account) {
      alert("Connect your wallet first!");
      return;
    }
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert("Please enter a valid positive amount!");
      return;
    }
    if (!ethers.isAddress(spenderAddress)) {
      alert("Please enter a valid address!");
      return;
    }
    try {
      const contract = await getContract();
      const amountInBaseUnits = ethers.parseEther(amount);
      const tx = await contract.approve(spenderAddress, amountInBaseUnits);
      await tx.wait();
      alert(`${selectedToken} Approval successful!`);
      setSpenderAddress("");
      setAmount("");
    } catch (error) {
      console.error(`${selectedToken} Approval failed:`, error);
      alert(`${selectedToken} Approval failed: ${error.message}`);
    }
  };

  const handleTransferFrom = async () => {
    if (!provider || !account) {
      alert("Connect your wallet first!");
      return;
    }
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert("Please enter a valid positive amount!");
      return;
    }
    if (!ethers.isAddress(fromAddress) || !ethers.isAddress(toAddress)) {
      alert("Please enter valid addresses!");
      return;
    }
    try {
      const contract = await getContract();
      const amountInBaseUnits = ethers.parseEther(amount);
      const tx = await contract.transferFrom(fromAddress, toAddress, amountInBaseUnits);
      await tx.wait();
      alert(`${selectedToken} Transfer From successful!`);
      setFromAddress("");
      setToAddress("");
      setAmount("");
    } catch (error) {
      console.error(`${selectedToken} Transfer From failed:`, error);
      alert(`${selectedToken} Transfer From failed: ${error.message}`);
    }
  };

  return (
    <div>
      <h2 className={selectedToken.toLowerCase()}>
        <FaExchangeAlt className="icon" /> Transfer
      </h2>
      <p>Transaction Fee for {selectedToken}: {transactionFee}%</p>
      <div className="tab-container">
        <button
          className={activeTab === "transfer" ? "active-tab" : ""}
          onClick={() => setActiveTab("transfer")}
        >
          Transfer
        </button>
        <button
          className={activeTab === "approve" ? "active-tab" : ""}
          onClick={() => setActiveTab("approve")}
        >
          Approve
        </button>
        <button
          className={activeTab === "transferFrom" ? "active-tab" : ""}
          onClick={() => setActiveTab("transferFrom")}
        >
          Transfer From
        </button>
      </div>

      <div className="token-selector">
        <label>Select Token: </label>
        <select
          value={selectedToken}
          onChange={(e) => setSelectedToken(e.target.value)}
          className={selectedToken.toLowerCase()}
        >
          <option value="CRW">CRW</option>
          <option value="CUSD">CUSD</option>
          <option value="DEUS">DEUS</option>
        </select>
      </div>

      {activeTab === "transfer" && (
        <div className={`action-box ${selectedToken.toLowerCase()}`}>
          <h3>Transfer {selectedToken}</h3>
          <input
            type="text"
            placeholder="To Address"
            value={toAddress}
            onChange={(e) => setToAddress(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <input
            type="text"
            placeholder={`Amount (${selectedToken})`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <button onClick={handleTransfer}>Transfer</button>
        </div>
      )}

      {activeTab === "approve" && (
        <div className={`action-box ${selectedToken.toLowerCase()}`}>
          <h3>Approve {selectedToken}</h3>
          <input
            type="text"
            placeholder="Spender Address"
            value={spenderAddress}
            onChange={(e) => setSpenderAddress(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <input
            type="text"
            placeholder={`Amount (${selectedToken})`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <button onClick={handleApprove}>Approve</button>
        </div>
      )}

      {activeTab === "transferFrom" && (
        <div className={`action-box ${selectedToken.toLowerCase()}`}>
          <h3>Transfer From {selectedToken}</h3>
          <input
            type="text"
            placeholder="From Address"
            value={fromAddress}
            onChange={(e) => setFromAddress(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <input
            type="text"
            placeholder="To Address"
            value={toAddress}
            onChange={(e) => setToAddress(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <input
            type="text"
            placeholder={`Amount (${selectedToken})`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={selectedToken.toLowerCase()}
          />
          <button onClick={handleTransferFrom}>Transfer From</button>
        </div>
      )}
    </div>
  );
};

export default TransferActions;